import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import type { Store } from '../../lib/supabase'
import { SheetPanel } from '../SheetPanel'

// Before shopping mode (and from its store name): where are you? The store
// decides the order of the list (lib/storeRoute.ts), so the choice is one tap —
// the last store is already picked — and a new store is a name away.
// "Somewhere else" shops without a store: category order, nothing learned.

export function StorePicker({ open, stores, selected, starting, onPick, onAddStore, onClose }: {
  open: boolean
  stores: Store[]
  /** The store picked when it opens: the trip's, or the last one on this phone. */
  selected: string | null
  /** True before a trip (button says Start shopping), false when changing store mid-trip. */
  starting: boolean
  onPick: (storeId: string | null) => void
  onAddStore: (name: string) => Promise<Store | null>
  onClose: () => void
}) {
  return (
    <AnimatePresence>
      {open && (
        <SheetPanel onClose={onClose} ariaLabel="Where are you shopping?" zIndex={30} layout="scroll">
          <Picker stores={stores} selected={selected} starting={starting} onPick={onPick} onAddStore={onAddStore} />
        </SheetPanel>
      )}
    </AnimatePresence>
  )
}

const ELSEWHERE = '__elsewhere'

function Picker({ stores, selected, starting, onPick, onAddStore }: {
  stores: Store[]
  selected: string | null
  starting: boolean
  onPick: (storeId: string | null) => void
  onAddStore: (name: string) => Promise<Store | null>
}) {
  const [choice, setChoice] = useState<string>(selected ?? (stores.length ? stores[0].id : ELSEWHERE))
  const [name, setName] = useState('')
  const [adding, setAdding] = useState(false)

  const add = async () => {
    if (!name.trim() || adding) return
    setAdding(true)
    const store = await onAddStore(name)
    setAdding(false)
    if (store) {
      setChoice(store.id)
      setName('')
    }
  }

  return (
    <div className="ml-stores">
      <h2 className="ml-display ml-sheet-title">Where are you shopping?</h2>
      <div className="ml-stores__list" role="radiogroup" aria-label="Store">
        {stores.map(s => (
          <label key={s.id} className={`ml-stores__option${choice === s.id ? ' is-on' : ''}`}>
            <input type="radio" name="store" value={s.id} checked={choice === s.id} onChange={() => setChoice(s.id)} />
            <span className="ml-stores__name">{s.name}</span>
            {s.route.length > 0 && <span className="ml-caption">Knows {s.route.length === 1 ? '1 item' : `${s.route.length} items`}</span>}
          </label>
        ))}
        <label className={`ml-stores__option${choice === ELSEWHERE ? ' is-on' : ''}`}>
          <input type="radio" name="store" value={ELSEWHERE} checked={choice === ELSEWHERE} onChange={() => setChoice(ELSEWHERE)} />
          <span className="ml-stores__name">Somewhere else</span>
          <span className="ml-caption">In category order</span>
        </label>
      </div>

      <form className="ml-stores__add" onSubmit={e => { e.preventDefault(); void add() }}>
        <input className="ml-input" aria-label="New store" placeholder="Add a store, like ICA Maxi" maxLength={40}
          value={name} onChange={e => setName(e.target.value)} />
        <button type="submit" className="ml-btn ml-btn--secondary" disabled={!name.trim() || adding}>Add</button>
      </form>
      <p className="ml-caption">Matlist learns each store’s order from the order you put things in the basket.</p>

      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide"
        onClick={() => onPick(choice === ELSEWHERE ? null : choice)}>
        {starting ? 'Start shopping' : 'Shop here'}
      </button>
    </div>
  )
}
